import { PnLNode } from '../../../../types/PnLNode';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import { useState, useEffect } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { getPnLCache, setPnLCache } from './pnlCache';
import { useNodePreview } from '../../../../hooks/useNodePreview';
import { useDataTypes } from '../../../../hooks/useDataTypes';
import { findUnderlyingPriceNode } from '../../../../utils/hoistPriceSource';

interface Props {
    node: PnLNode;
}

import "./ComponentCard.css";

export function PnLComponentCard({ node }: Props) {
    const { onUpdate, globalStart, globalEnd, isInteractionLocked, updateNodeMetadata, updatePreviewResult } = useNodeOperations();
    const { dataTypes } = useDataTypes();
    const { config } = node;

    const [localMaxLots, setLocalMaxLots] = useState(String(config.maxLots ?? 1));
    const [localLotSize, setLocalLotSize] = useState(String(config.lotSize ?? 1));
    const [localFees, setLocalFees] = useState(String(config.fees ?? 0));

    // ✅ Using the shared hook for preview calculation
    const {
        isCalculating,
        errorMsg,
        result: chartData,
        setErrorMsg,
        setResult: setChartData,
        calculatePreview: handleCalculate
    } = useNodePreview<any[]>(node, globalStart || null, globalEnd || null, updateNodeMetadata, updatePreviewResult);

    // ✅ Sync with cache
    useEffect(() => {
        const cached = getPnLCache(node.id);
        if (cached?.chartData?.length) {
            setChartData(cached.chartData);
            setErrorMsg(cached.errorMsg || null);
        }
    }, [node.id, setChartData, setErrorMsg]);

    useEffect(() => {
        if (chartData) {
            setPnLCache(node.id, chartData, errorMsg);
        }
    }, [node.id, chartData, errorMsg]);

    useEffect(() => {
        setLocalMaxLots(String(config.maxLots ?? 1));
        setLocalLotSize(String(config.lotSize ?? 1));
        setLocalFees(String(config.fees ?? 0));
    }, [config.maxLots, config.lotSize, config.fees]);

    const commitUpdate = (field: string, value: any) => {
        if (isInteractionLocked) {
            return;
        }
        const updated = new PnLNode(
            node.id,
            node.title,
            config.entry,
            config.exit,
            field === 'maxLots' ? Number(value) : config.maxLots,
            field === 'lotSize' ? Number(value) : config.lotSize,
            field === 'fees' ? Number(value) : config.fees,
            field === 'mode' ? value : config.mode,
            node.isManualRename,
            field === 'exitMode' ? value : (config.exitMode || 'signal'),
            field === 'holdDuration' ? Number(value) : (config.holdDuration || 1),
            field === 'holdType' ? value : (config.holdType || 'hour')
        );
        onUpdate(node.id, updated);
    };

    const handleToggleExitMode = (m: 'signal' | 'hold') => {
        if (isInteractionLocked) {
            return;
        }
        let newExit = config.exit;
        if (m === 'hold' && !config.exit && config.entry) {
            const suggested = findUnderlyingPriceNode(config.entry, dataTypes);
            if (suggested) {
                newExit = suggested;
            }
        }

        const updated = new PnLNode(
            node.id,
            node.title,
            config.entry,
            newExit,
            config.maxLots,
            config.lotSize,
            config.fees,
            config.mode,
            node.isManualRename,
            m,
            config.holdDuration || 1,
            config.holdType || 'hour'
        );
        onUpdate(node.id, updated);
    };

    const stopPropagation = (e: React.UIEvent) => {
        e.stopPropagation();
    };

    const formatPnL = (val: number) => {
        if (Math.abs(val) >= 1_000_000) return (val / 1_000_000).toFixed(2) + 'm';
        if (Math.abs(val) >= 1_000) return (val / 1_000).toFixed(1) + 'k';
        return val.toFixed(2);
    };

    const finalPnL = chartData && chartData.length > 0 ? chartData[chartData.length - 1].value : null;

    return (
        <div className="internal-card-container" style={{ minWidth: '320px', maxWidth: '420px' }}>

            {/* Settings Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.8rem', justifyContent: 'start', marginBottom: '0.4rem' }}>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Max Lots</label>
                    <input
                        type="number"
                        min="1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localMaxLots}
                        onChange={e => {
                            setLocalMaxLots(e.target.value);
                            commitUpdate('maxLots', e.target.value);
                        }}
                        onBlur={e => commitUpdate('maxLots', e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitUpdate('maxLots', e.currentTarget.value) }}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        onWheel={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Lot Size</label>
                    <input
                        type="number"
                        min="1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localLotSize}
                        onChange={e => {
                            setLocalLotSize(e.target.value);
                            commitUpdate('lotSize', e.target.value);
                        }}
                        onBlur={e => commitUpdate('lotSize', e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitUpdate('lotSize', e.currentTarget.value) }}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        onWheel={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Fees (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localFees}
                        onChange={e => {
                            setLocalFees(e.target.value);
                            commitUpdate('fees', e.target.value);
                        }}
                        onBlur={e => commitUpdate('fees', e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitUpdate('fees', e.currentTarget.value) }}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        onWheel={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
            </div>

            {/* Side & Exit Mode Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.4rem', marginBottom: '0.8rem' }}>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Side</label>
                    <div className="segmented-control">
                        {(['long', 'short'] as const).map(m => (
                            <button
                                key={m}
                                onClick={() => commitUpdate('mode', m)}
                                disabled={isInteractionLocked}
                                className={`segmented-btn ${config.mode === m ? 'active' : ''}`}
                            >
                                {m === 'long' ? 'Long' : 'Short'}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col gap-xs">
                    <label className="field-label">Exit Rule</label>
                    <div className="segmented-control">
                        {(['signal', 'hold'] as const).map(m => (
                            <button
                                key={m}
                                onClick={() => handleToggleExitMode(m)}
                                disabled={isInteractionLocked}
                                className={`segmented-btn ${config.exitMode === m ? 'active' : ''}`}
                            >
                                {m === 'signal' ? 'Signal' : 'Hold'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Hold Configuration */}
            {config.exitMode === 'hold' && (
                    <div className="flex gap-xs">
                        <div className="flex flex-col gap-xs" style={{ flex: 1 }}>
                            <label className="field-label">Duration</label>
                            <input
                                type="number"
                                min="1"
                                className="input-number-premium pan-zoom-exclude nodrag"
                                value={config.holdDuration || 1}
                                onChange={e => commitUpdate('holdDuration', e.target.value)}
                                onPointerDown={stopPropagation}
                                onMouseDown={stopPropagation}
                                disabled={isInteractionLocked}
                            />
                        </div>
                        <div className="flex flex-col gap-xs" style={{ flex: 1.5 }}>
                            <label className="field-label">Unit</label>
                            <select
                                className="input-number-premium pan-zoom-exclude nodrag"
                                style={{ padding: '4px', fontSize: '0.7rem' }}
                                value={config.holdType || 'hour'}
                                onChange={e => commitUpdate('holdType', e.target.value)}
                                onPointerDown={stopPropagation}
                                onMouseDown={stopPropagation}
                                disabled={isInteractionLocked}
                            >
                                <option value="minute">Minutes</option>
                                <option value="hour">Hours</option>
                                <option value="day">Days</option>
                            </select>
                        </div>
                    </div>
                )}

            {/* Chart Section */}
            <div className="flex flex-col gap-xs">
                <div className="flex justify-between items-center bg-panel p-xs rounded-sm mb-xs">
                    <span className="field-label">Realized PnL</span>
                    <div className="flex gap-xs">
                        <button
                            onClick={() => {
                                if (!isInteractionLocked) {
                                    void handleCalculate();
                                }
                            }}
                            disabled={isCalculating || isInteractionLocked}
                            className="action-btn"
                            title="Calculate Preview Natively"
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                        >
                            {isCalculating ? (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                                    <line x1="12" y1="2" x2="12" y2="6"></line>
                                    <line x1="12" y1="18" x2="12" y2="22"></line>
                                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                                    <line x1="2" y1="12" x2="6" y2="12"></line>
                                    <line x1="18" y1="12" x2="22" y2="12"></line>
                                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                                    <line x1="16.24" y1="4.93" x2="19.07" y2="7.76"></line>
                                </svg>
                            ) : (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <polyline points="1 20 1 14 7 14"></polyline>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                                </svg>
                            )}
                            Preview
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isInteractionLocked) {
                                    return;
                                }
                                const updated = new PnLNode(
                                    node.id,
                                    node.title,
                                    config.exit, // Swapped
                                    config.entry,  // Swapped
                                    config.maxLots,
                                    config.lotSize,
                                    config.fees,
                                    config.mode,
                                    node.isManualRename,
                                    config.exitMode,
                                    config.holdDuration,
                                    config.holdType
                                );
                                onUpdate(node.id, updated);
                            }}
                            disabled={isInteractionLocked}
                            className="action-btn"
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                            title="Swap Entry and Exit signals"
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="16 3 21 3 21 8"></polyline>
                                <line x1="4" y1="20" x2="21" y2="3"></line>
                                <polyline points="21 16 21 21 16 21"></polyline>
                                <line x1="15" y1="15" x2="21" y2="21"></line>
                                <line x1="4" y1="4" x2="9" y2="9"></line>
                            </svg>
                            Swap
                        </button>
                    </div>
                </div>
                {/* Chart Container */}
                <div style={{
                    width: '100%',
                    height: '140px',
                    background: 'var(--surface-dark-medium)',
                    border: errorMsg ? '1px solid var(--accent-danger)' : '1px solid var(--surface-glass-border)',
                    borderRadius: 'var(--radius-md)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'var(--shadow-inset-strong)'
                }}>
                    {errorMsg ? (
                        <div style={{ color: 'var(--accent-danger)', fontSize: '0.75rem', textAlign: 'center', padding: '1rem' }}>
                            {errorMsg}
                        </div>
                    ) : (chartData && chartData.length > 0) ? (
                        <>
                            {finalPnL !== null && (
                                <div style={{
                                    position: 'absolute',
                                    top: '8px', left: '12px',
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: finalPnL >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)',
                                    zIndex: 10,
                                    textShadow: 'var(--shadow-text-strong)'
                                }}>
                                    {(finalPnL >= 0 ? '+' : '') + formatPnL(finalPnL)}
                                </div>
                            )}
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData || []} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--accent-success)" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="var(--accent-success)" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <Area
                                        type="stepAfter"
                                        dataKey="value"
                                        stroke="var(--accent-success)"
                                        fillOpacity={1}
                                        fill="url(#pnlFill)"
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                    <Tooltip
                                        contentStyle={{ background: 'var(--bg-app)', border: '1px solid var(--surface-glass-border)', fontSize: '0.8rem' }}
                                        labelFormatter={() => ''} // Hide time label for clean step view
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </>
                    ) : (
                        <>
                            {/* Flat 0 line */}
                            <div style={{
                                position: 'absolute',
                                bottom: '40px',
                                left: '0',
                                width: '100%',
                                height: '1px',
                                background: 'var(--surface-glass-border)',
                                opacity: 0.8
                            }} />

                            {/* Dummy Path */}
                            <svg width="100%" height="100%" viewBox="0 0 400 140" preserveAspectRatio="none">
                                <path
                                    d="M 0 100 L 120 100 L 120 40 L 260 40 L 260 70 L 400 70"
                                    fill="none"
                                    stroke="var(--accent-success)"
                                    strokeWidth="3"
                                    strokeOpacity="0.3"
                                    strokeDasharray="5,5"
                                    vectorEffect="non-scaling-stroke"
                                />
                            </svg>
                        </>
                    )}
                </div>
            </div>

        </div>
    );
}
